import React, { useEffect, useState } from 'react';
import { authService } from '../services/api';

const ROLES = ['admin', 'editor', 'viewer'];
const ROL_COLOR = { admin: '#6A2C75', editor: '#D6A644', viewer: '#7a6882' };
const ROL_LABEL = { admin: 'Administrador', editor: 'Editor', viewer: 'Solo lectura' };

export default function Usuarios() {
  const [usuarios, setUsuarios]   = useState([]);
  const [modulos, setModulos]     = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | 'nuevo' | usuario
  const [form, setForm]           = useState({});
  const [modulosForm, setModulosForm] = useState({});
  const [departamentosForm, setDepartamentosForm] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje]     = useState(null);
  const [error, setError]         = useState(null);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const [u, m, d] = await Promise.all([authService.usuarios(), authService.modulos(), authService.departamentos()]);
      setUsuarios(u.data.data || []);
      setModulos(m.data.data || []);
      setDepartamentos(d.data.data || []);
    } catch { setError('Error cargando datos'); }
    finally { setLoading(false); }
  };

  const abrirNuevo = () => {
    setForm({ nombre: '', email: '', password: '', rol: 'viewer' });
    setModulosForm({});
    setDepartamentosForm({});
    setModal('nuevo');
    setError(null);
  };

  const abrirEditar = (u) => {
    setForm({ nombre: u.nombre, email: u.email, rol: u.rol, activo: u.activo, password: '' });
    const mf = {};
    (u.modulos || []).forEach(m => { mf[m.modulo] = { ver: m.puede_ver, editar: m.puede_editar }; });
    setModulosForm(mf);
    const df = {};
    (u.departamentos || []).forEach(d => { df[d.clave] = { ver_resumen: d.ver_resumen, ver_detalle: d.ver_detalle, capturar: d.capturar }; });
    setDepartamentosForm(df);
    setModal(u);
    setError(null);
  };

  const toggleModulo = (clave, tipo) => {
    setModulosForm(prev => {
      const actual = prev[clave] || { ver: false, editar: false };
      if (tipo === 'ver') {
        const nuevoVer = !actual.ver;
        return { ...prev, [clave]: { ver: nuevoVer, editar: nuevoVer ? actual.editar : false } };
      }
      if (tipo === 'editar') {
        return { ...prev, [clave]: { ver: true, editar: !actual.editar } };
      }
      return prev;
    });
  };

  // A diferencia de los módulos (2 niveles: ver → editar), aquí hay 3
  // niveles independientes entre sí salvo por una regla: "capturar" y
  // "ver_detalle" implican automáticamente "ver_resumen" (no tiene
  // sentido poder capturar un KPI que ni siquiera puedes ver resumido).
  const toggleDepartamento = (clave, tipo) => {
    setDepartamentosForm(prev => {
      const actual = prev[clave] || { ver_resumen: false, ver_detalle: false, capturar: false };
      const nuevoValor = !actual[tipo];
      const implicaResumen = tipo !== 'ver_resumen' && nuevoValor;
      return {
        ...prev,
        [clave]: {
          ...actual,
          [tipo]: nuevoValor,
          ver_resumen: implicaResumen ? true : (tipo === 'ver_resumen' ? nuevoValor : actual.ver_resumen),
        },
      };
    });
  };

  const construirModulos = () =>
    Object.entries(modulosForm)
      .filter(([_, v]) => v.ver)
      .map(([clave, v]) => ({ clave, puede_ver: v.ver, puede_editar: v.editar }));

  const construirDepartamentos = () =>
    Object.entries(departamentosForm)
      .filter(([_, v]) => v.ver_resumen || v.ver_detalle || v.capturar)
      .map(([clave, v]) => ({ clave, ver_resumen: v.ver_resumen, ver_detalle: v.ver_detalle, capturar: v.capturar }));

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const payload = { ...form, modulos: construirModulos(), departamentos: construirDepartamentos() };
      if (!payload.password) delete payload.password;
      if (modal === 'nuevo') {
        await authService.crearUsuario(payload);
        setMensaje('Usuario creado correctamente');
      } else {
        await authService.actualizarUsuario(modal.id, payload);
        setMensaje('Usuario actualizado correctamente');
      }
      setModal(null);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const areaModulos = modulos.reduce((acc, m) => {
    if (!acc[m.area]) acc[m.area] = [];
    acc[m.area].push(m);
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#6A2C75' }}>Gestión de Usuarios</h1>
          <p style={{ color: '#7a6882', fontSize: 13, marginTop: 4 }}>{usuarios.length} usuarios registrados</p>
        </div>
        <button onClick={abrirNuevo} style={{
          background: '#6A2C75', color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          + Nuevo usuario
        </button>
      </div>

      {mensaje && (
        <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          ✅ {mensaje}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7a6882' }}>Cargando...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e0d5e8', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f3f0' }}>
                {['Usuario', 'Email', 'Rol', 'Departamentos', 'Estado', 'Último acceso', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#7a6882', borderBottom: '1px solid #e0d5e8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u, i) => (
                <tr key={u.id} style={{ background: i % 2 === 0 ? '#fff' : '#faf8fc' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#2a1a2e', fontSize: 14 }}>{u.nombre}</td>
                  <td style={{ padding: '12px 16px', color: '#7a6882', fontSize: 13 }}>{u.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: ROL_COLOR[u.rol] + '20', color: ROL_COLOR[u.rol], borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                      {ROL_LABEL[u.rol]}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#7a6882' }}>
                    {u.rol === 'admin' ? (
                      <span style={{ color: '#6A2C75', fontWeight: 600 }}>Todos</span>
                    ) : (
                      <span>{u.departamentos?.length || 0} departamento(s)</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: u.activo ? '#2e7d32' : '#c62828', fontSize: 12, fontWeight: 600 }}>
                      {u.activo ? '● Activo' : '● Inactivo'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#7a6882' }}>
                    {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => abrirEditar(u)} style={{ background: 'transparent', border: '1px solid #e0d5e8', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#6A2C75', fontWeight: 600 }}>
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#6A2C75', marginBottom: 24 }}>
              {modal === 'nuevo' ? 'Nuevo Usuario' : `Editar: ${modal.nombre}`}
            </h2>

            {error && (
              <div style={{ background: '#fdecea', color: '#c62828', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>
            )}

            {/* Datos básicos */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {[
                { label: 'Nombre completo', key: 'nombre', type: 'text', placeholder: 'Nombre Apellido' },
                { label: 'Correo electrónico', key: 'email', type: 'email', placeholder: 'correo@dasavena.com' },
                { label: modal === 'nuevo' ? 'Contraseña' : 'Nueva contraseña (opcional)', key: 'password', type: 'password', placeholder: '••••••••' },
              ].map(f => (
                <div key={f.key} style={{ gridColumn: f.key === 'nombre' ? 'span 2' : 'span 1' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#7a6882', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>{f.label.toUpperCase()}</label>
                  <input
                    type={f.type} value={form[f.key] || ''} placeholder={f.placeholder}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e0d5e8', fontSize: 14, outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = '#6A2C75'}
                    onBlur={e => e.target.style.borderColor = '#e0d5e8'}
                  />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#7a6882', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>ROL</label>
                <select value={form.rol || 'viewer'} onChange={e => setForm(p => ({ ...p, rol: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e0d5e8', fontSize: 14, outline: 'none', background: '#fff' }}>
                  {ROLES.map(r => <option key={r} value={r}>{ROL_LABEL[r]}</option>)}
                </select>
              </div>
            </div>

            {/* Departamentos — solo si no es admin */}
            {form.rol !== 'admin' && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7a6882', letterSpacing: 0.5, marginBottom: 12 }}>ACCESO A DEPARTAMENTOS (KPIs)</div>
                {departamentos.map(dep => {
                  const estado = departamentosForm[dep.clave] || { ver_resumen: false, ver_detalle: false, capturar: false };
                  const activo = estado.ver_resumen || estado.ver_detalle || estado.capturar;
                  return (
                    <div key={dep.clave} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: activo ? '#f5f0fa' : '#f9f9f9', marginBottom: 6, border: `1px solid ${activo ? '#e0d5e8' : '#eee'}` }}>
                      <span style={{ fontSize: 13, color: '#2a1a2e', fontWeight: 600 }}>{dep.nombre}</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { tipo: 'ver_resumen', label: 'Ver KPI' },
                          { tipo: 'ver_detalle', label: 'Ver detalle' },
                          { tipo: 'capturar', label: 'Capturar' },
                        ].map(btn => (
                          <button key={btn.tipo} onClick={() => toggleDepartamento(dep.clave, btn.tipo)}
                            style={{
                              padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                              background: estado[btn.tipo] ? '#6A2C75' : '#e0d5e8',
                              color: estado[btn.tipo] ? '#fff' : '#7a6882',
                            }}>
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Módulos — solo si no es admin */}
            {form.rol !== 'admin' && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7a6882', letterSpacing: 0.5, marginBottom: 12 }}>ACCESO A MÓDULOS</div>
                {Object.entries(areaModulos).map(([area, mods]) => (
                  <div key={area} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6A2C75', marginBottom: 8 }}>{area}</div>
                    {mods.map(m => {
                      const estado = modulosForm[m.clave] || { ver: false, editar: false };
                      return (
                        <div key={m.clave} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: estado.ver ? '#f5f0fa' : '#f9f9f9', marginBottom: 6, border: `1px solid ${estado.ver ? '#e0d5e8' : '#eee'}` }}>
                          <span style={{ fontSize: 13, color: '#2a1a2e' }}>{m.icono} {m.nombre}</span>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[{ tipo: 'ver', label: 'Ver' }, { tipo: 'editar', label: 'Editar' }].map(btn => (
                              <button key={btn.tipo} onClick={() => toggleModulo(m.clave, btn.tipo)}
                                style={{
                                  padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                                  background: (btn.tipo === 'ver' ? estado.ver : estado.editar) ? '#6A2C75' : '#e0d5e8',
                                  color: (btn.tipo === 'ver' ? estado.ver : estado.editar) ? '#fff' : '#7a6882',
                                }}>
                                {btn.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {/* Estado (solo edición) */}
            {modal !== 'nuevo' && (
              <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="activo" checked={form.activo ?? true} onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))} />
                <label htmlFor="activo" style={{ fontSize: 13, color: '#2a1a2e' }}>Usuario activo</label>
              </div>
            )}

            {/* Botones */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #e0d5e8', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#7a6882' }}>
                Cancelar
              </button>
              <button onClick={guardar} disabled={guardando} style={{
                padding: '10px 24px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700, cursor: guardando ? 'default' : 'pointer',
                background: guardando ? '#BBA4C0' : '#6A2C75', color: '#fff',
              }}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}